import axios from 'services/axios';
import { EPropMyChallengesTypes } from './actionsTypes';
import { batch } from 'react-redux';
import { RootState } from 'store/index';
import { user_changeSelectedTpByName, user_refreshUserData, user_updateStoreUser } from 'store/user/actions';
import {
    AccountInfoParsed,
    AccountInfoParsedKycVerificationStatusEnum,
    KeeperDetail,
    ManualKycRequest,
    PropChallenge,
    PropChallengeChallengeStatusEnum,
    PropChallengeChallengeTypeEnum,
    PropPlan,
    ResultInfoCodeEnum,
} from 'services/generatedClientFromSwagger';
import { isTablet } from 'helpers/device_helper';
import { KycDialogOpen } from 'features/prop/interfaces';
import { isKeeperGuard, KeeperDetailWithType, KeeperType, KeepModalType, PlanOrKeeper } from './interfaces';
import { verifyChallengeKeeper } from 'utils/keeper.utils';
import { doesChallengeRequireActivationFlow } from 'features/prop/utils/challengeKyc';

const isValidChallenge = (challenge: PropChallenge) => {
    if (!challenge.tpName) return false;
    return true;
};

export const prop__myPlans_getPlans = (withLoading: boolean = true, update?: boolean) => {
    return async (dispatch, getState) => {
        try {
            const state: RootState = getState();
            const tpAccounts = state.user.userInfo.tradingPlatformAccounts;
            const accountId = state.user?.userInfo?.id;

            dispatch({ type: EPropMyChallengesTypes.GET_PLANS_START });
            dispatch({ type: EPropMyChallengesTypes.GET_CHALLENGES_START, payload: { withLoading } });
            const plans = (await axios.PropApi.propApiControllerGetPlans(update))?.data?.plans;

            dispatch(prop__plans_purchaseKeeper(plans ?? []));

            dispatch({ type: EPropMyChallengesTypes.GET_PLANS_SUCCESS, payload: plans });

            if (!plans) {
                return;
            }

            const challenges = plans.reduce((acc: PropChallenge[], plan) => {
                plan?.propChallenges && acc.push(...plan?.propChallenges);
                return acc;
            }, []);
            const fundsFilterOptions: Array<any> = [];
            if (!isTablet()) {
                fundsFilterOptions.push(...(new Set(challenges.map((challenge) => challenge.funds)) ?? []));
            }

            const { filteredChallenges, onlyFundedChallenges } = challenges.reduce(
                (
                    acc: { filteredChallenges: PropChallenge[]; onlyFundedChallenges: PropChallenge[] },
                    challenge: PropChallenge
                ) => {
                    if (!isValidChallenge(challenge)) {
                        return acc;
                    }
                    acc.filteredChallenges.push(challenge);
                    if (challenge.challengeType === PropChallengeChallengeTypeEnum.Funded) {
                        acc.onlyFundedChallenges.push(challenge);
                    }
                    return acc;
                },
                { filteredChallenges: [], onlyFundedChallenges: [] }
            );

            if (filteredChallenges.length + 1 !== tpAccounts?.length && Boolean(accountId)) {
                await dispatch(user_refreshUserData());
            }
            dispatch({
                type: EPropMyChallengesTypes.GET_CHALLENGES_SUCCESS,
                payload: {
                    challenges,
                    filteredChallenges,
                    fundsFilterOptions,
                    onlyFundedChallenges,
                },
            });
            const challengeTpName = localStorage.getItem('selectedChallengeTp');

            const selectedChallenge = filteredChallenges.find((c) => {
                return c.tpName === challengeTpName;
            });

            const firstChallenge = filteredChallenges.find((c) => {
                return c.tpName !== challengeTpName;
            });

            const selectedChallengeTpName =
                selectedChallenge?.tpName || firstChallenge?.tpName || filteredChallenges[0]?.tpName || '';

            dispatch(prop__changeSelectedTpChallenge(selectedChallengeTpName));
            return plans;
        } catch (error) {
            dispatch({ type: EPropMyChallengesTypes.GET_CHALLENGES_FAIL });
        }
    };
};

export const prop__myPlans_validateWithdrawal = (challengeId: string, tpAccount: string) => {
    return async (dispatch) => {
        try {
            const validateWithdrawal = await axios.PropApi.propApiControllerGetChallengeValidateWithdrawal({
                challengeId,
                tpAccount,
            });
            dispatch({
                type: EPropMyChallengesTypes.VALIDATE_WITHDRAWAL_SUCCESS,
                payload: { data: validateWithdrawal.data, tpAccount },
            });
        } catch (error) {
            return false;
        }
    };
};

export const prop__myPlans_getChallengeIframeUrl = (tpId: string) => {
    return async (dispatch) => {
        try {
            dispatch({ type: EPropMyChallengesTypes.GET_CHALLENGE_IFRAME_START, payload: tpId });
            const getIframeRes = await axios.PropApi.propApiControllerGetChallengeIframeUrl(tpId);
            dispatch({
                type: EPropMyChallengesTypes.GET_CHALLENGE_IFRAME_SUCCESS,
                payload: { tpId: tpId, iframeUrl: getIframeRes.data },
            });
        } catch (error) {
            return batch(() => {
                dispatch({ type: EPropMyChallengesTypes.GET_CHALLENGE_IFRAME_FAIL, payload: tpId });
            });
        }
    };
};

export const prop__changeSelectedTpChallenge = (tpName: string) => {
    return async (dispatch, getState) => {
        const state: RootState = getState();
        if (state.prop_myPlans.filteredChallenges.length) {
            const selectedChallenge =
                state.prop_myPlans.filteredChallenges.find((challenge) => challenge.tpName === tpName) ||
                state.prop_myPlans.filteredChallenges[0];

            const selectedTpName = selectedChallenge.tpName as string;
            const selectedChallengePlan = state?.prop_myPlans?.plans?.find?.(
                (plan) => plan.pPlan?.planId === selectedChallenge.plan
            );

            if (verifyChallengeKeeper(selectedChallenge)) {
                dispatch(prop__plans_openKeepAlertPopup(true, selectedChallenge, selectedChallengePlan));
            }
            dispatch({
                type: EPropMyChallengesTypes.SET_SELECTED_CHALLENGE,
                payload: { selectedChallenge, selectedChallengePlan },
            });
            if (selectedTpName !== state.user.selectedTPAccount?.name) {
                dispatch(user_changeSelectedTpByName(selectedTpName));
            }
            localStorage.setItem('selectedChallengeTp', selectedTpName);
        }
    };
};

export const prop__setMainTpAccount = (userInfo: AccountInfoParsed) => {
    return async (dispatch) => {
        const mainAccountTp = userInfo.tradingPlatformAccounts?.reduce((prev, current) => {
            return Number(prev.name) < Number(current.name) ? prev : current;
        });
        dispatch({ type: EPropMyChallengesTypes.SET_MAIN_ACCOUNT_TP, payload: mainAccountTp });
    };
};

export const prop__myPlans_iframePopup = (isOpen: boolean) => {
    return async (dispatch) => {
        try {
            dispatch({
                type: isOpen
                    ? EPropMyChallengesTypes.GET_CHALLENGE_IFRAME_POPUP_SUCCESS
                    : EPropMyChallengesTypes.GET_CHALLENGE_IFRAME_POPUP_FAIL,
            });
        } catch (error) {
            dispatch({ type: EPropMyChallengesTypes.GET_CHALLENGE_IFRAME_POPUP_FAIL });
        }
    };
};
export const prop_handleChallengeStartFundedAlert = (challengeId: string, isOpen: boolean, sent: boolean) => {
    return async (dispatch, getState) => {
        const state: RootState = getState();
        const userInfo = state?.user?.userInfo;
        if (userInfo?.kycVerificationStatus === AccountInfoParsedKycVerificationStatusEnum.InProgress) {
            dispatch(user_refreshUserData());
        }
        dispatch({
            type: EPropMyChallengesTypes.SET_FUNDED_ALERT_TOGGLE,
            payload: { [challengeId]: { display: isOpen, sent } },
        });
    };
};
export const prop__myPlans_acceptChallengeTermsAndCondition = (challengeId: string) => {
    return async (dispatch) => {
        try {
            dispatch(prop_handleChallengeStartFundedAlert(challengeId, false, true));
            dispatch({ type: EPropMyChallengesTypes.ACCEPT_CHALLENGE_TERMS_START, payload: challengeId });
            const acceptChallengeTermsAndCondition = await axios.PropApi.propApiControllerAcceptTermsAndConditions({
                challengeId,
            });
            dispatch(user_refreshUserData());
            dispatch({
                type: EPropMyChallengesTypes.ACCEPT_CHALLENGE_TERMS_SUCCESS,
                payload: {
                    challengeId,
                    acceptTermsAndConditions: acceptChallengeTermsAndCondition.data.code === ResultInfoCodeEnum.Success,
                },
            });
            // Refresh plans/challenges so latest challengeStatus/tpName are reflected after accepting terms
            await dispatch(prop__myPlans_getPlans(false, true));
        } catch (error) {
            dispatch(prop_handleChallengeStartFundedAlert(challengeId, true, false));
            return batch(() => {
                dispatch({ type: EPropMyChallengesTypes.ACCEPT_CHALLENGE_TERMS_FAIL, payload: challengeId });
            });
        }
    };
};

export const prop_handleKycDialogState = (kycDialogState?: KycDialogOpen) => {
    return async (dispatch) => {
        dispatch({ type: EPropMyChallengesTypes.SET_KYC_DIALOG_STATE, payload: kycDialogState });
    };
};

export const prop_handleKycDialogStateFromNotification = () => {
    return async (dispatch, getState) => {
        const state: RootState = getState();

        let challengeToActivate: PropChallenge | null = null;
        for (let index = 0; index < state?.prop_myPlans?.plans?.length; index++) {
            const plan = state.prop_myPlans.plans[index];
            if (plan?.propChallenges?.length) {
                for (let index = 0; index < plan?.propChallenges?.length; index++) {
                    const challenge = plan?.propChallenges?.[index];
                    if (
                        challenge.challengeStatus === PropChallengeChallengeStatusEnum.NotStarted &&
                        doesChallengeRequireActivationFlow(challenge)
                    ) {
                        challengeToActivate = challenge;
                        break;
                    }
                }
            }
            if (challengeToActivate) {
                break;
            }
        }

        if (challengeToActivate) {
            dispatch({
                type: EPropMyChallengesTypes.SET_KYC_DIALOG_STATE,
                payload: { id: challengeToActivate.id, tpName: challengeToActivate.tpName },
            });
        }
    };
};

export const prop__myPlans_updateStatusManualKyc = (manualKycRequest: ManualKycRequest) => {
    return async (dispatch) => {
        try {
            dispatch({ type: EPropMyChallengesTypes.STATUS_MANUAL_KYC_START });
            const statusManualKyc = await axios.PropApi.propApiControllerManualKyc(manualKycRequest);

            if (statusManualKyc.data.updateRes.updateAccountDetailsResult?.accountInfo?.kycVerificationStatus) {
                dispatch(
                    user_updateStoreUser({
                        kycVerificationStatus:
                            AccountInfoParsedKycVerificationStatusEnum[
                                statusManualKyc.data.updateRes.updateAccountDetailsResult?.accountInfo?.kycVerificationStatus?.toString()
                            ],
                    })
                );
                dispatch(user_refreshUserData() as any);
            }
            dispatch({
                type: EPropMyChallengesTypes.STATUS_MANUAL_KYC_SUCCESS,
            });
        } catch (error) {
            return batch(() => {
                dispatch({ type: EPropMyChallengesTypes.STATUS_MANUAL_KYC_FAIL });
            });
        }
    };
};

export const prop_getAdditionalInfoFromMonetaryTransactions = (tpId: string) => {
    return async (dispatch) => {
        try {
            dispatch({ type: EPropMyChallengesTypes.GET_ADDITIONAL_INFO_FROM_MONETARY_TRANSACTIONS });
            const additionalInfos = (
                await axios.PropApi.propApiControllerGetAdditionalInfoFromMonetaryTransactions({
                    tpId,
                })
            ).data;
            dispatch({
                type: EPropMyChallengesTypes.GET_ADDITIONAL_INFO_FROM_MONETARY_TRANSACTIONS_SUCCESS,
                payload: additionalInfos,
            });
        } catch (error) {
            return batch(() => {
                dispatch({ type: EPropMyChallengesTypes.GET_ADDITIONAL_INFO_FROM_MONETARY_TRANSACTIONS_FAILURE });
            });
        }
    };
};

export const prop__plans_openKeepAlertPopup = (
    isOpen: boolean,
    selectedChallenge?: PropChallenge,
    selectedChallengePlan?: PropPlan
) => {
    return async (dispatch) => {
        dispatch({
            type: EPropMyChallengesTypes.SET_KEEPER_ALERT_POPUP_STATE,
            payload: { isOpen, selectedChallenge, selectedChallengePlan },
        });
    };
};

export const prop__plans_openKeepPurchasePopup = (
    isOpen: boolean,
    type: KeepModalType,
    selectedChallenge?: PropChallenge,
    selectedChallengePlan?: PropPlan
) => {
    return async (dispatch) => {
        dispatch({
            type: EPropMyChallengesTypes.SET_KEEPER_PURCHASE_POPUP_STATE,
            payload: { isOpen, type: type, selectedChallenge, selectedChallengePlan },
        });
    };
};

export const prop__plans_closeKeepAlertPopup = () => {
    return async (dispatch) => {
        dispatch({
            type: EPropMyChallengesTypes.SET_KEEPER_ALERT_POPUP_STATE,
            payload: { isOpen: false },
        });
    };
};

export const prop__plans_closeKeepPurchasePopup = () => {
    return async (dispatch) => {
        dispatch({
            type: EPropMyChallengesTypes.SET_KEEPER_PURCHASE_POPUP_STATE,
            payload: { isOpen: false },
        });
    };
};

export const prop__plans_setKeeperModalOpened = (keeperType: KeeperType) => {
    return async (dispatch) => {
        dispatch({
            type: EPropMyChallengesTypes.SET_KEEPER_MODAL_OPENED,
            payload: { keeperType },
        });
    };
};

export const prop__plans_closeKeepBannerPopup = () => {
    return async (dispatch) => {
        dispatch({
            type: EPropMyChallengesTypes.SET_KEEPER_BANNER_POPUP_STATE,
            payload: { isOpen: false },
        });
    };
};

export const prop__plans_purchaseKeeper = (plans: Array<PropPlan>) => {
    return async (dispatch) => {
        const myPurchases: Array<PlanOrKeeper> = [];

        for (const plan of plans) {
            myPurchases.push(plan);
            const challenges = plan?.propChallenges || [];
            for (let i = 0; i < challenges.length; i++) {
                const challenge = challenges[i];
                const keepers: Record<string, KeeperDetailWithType> = {};
                for (const keeperType in challenge.keeper) {
                    if ((challenge?.keeper[keeperType] as KeeperDetail)?.purchased === true) {
                        const keeper = challenge.keeper[keeperType] as KeeperDetail;
                        const propChallenges = [challenge];
                        if (i > 0) {
                            propChallenges.push(challenges[challenges.length - 1]);
                        }
                        const purchasedKeeper: KeeperDetailWithType = {
                            ...keeper,
                            isKeeperPurchase: true,
                            keeperTypes: [keeperType as KeeperType],
                            challengeId: challenge.id,
                            amount: keeper.priceValue,
                            packageName: challenge.packageName,
                            propChallenges: propChallenges,
                            pPlan: plan.pPlan,
                            originalAdditionalInfo: plan.originalAdditionalInfo,
                        };

                        if (keepers[purchasedKeeper.purchaseDate || '']) {
                            keepers[purchasedKeeper.purchaseDate || ''].keeperTypes.push(keeperType as KeeperType);
                            keepers[purchasedKeeper.purchaseDate || ''].amount =
                                (keepers[purchasedKeeper.purchaseDate || ''].amount || 0) + (keeper.priceValue || 0);
                        } else {
                            keepers[purchasedKeeper.purchaseDate || ''] = purchasedKeeper;
                        }
                    }
                }
                myPurchases.push(...Object.values(keepers));
            }
        }
        dispatch({
            type: EPropMyChallengesTypes.SET_PURCHASED_KEEPERS,
            payload: myPurchases,
        });
    };
};
